import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { epApi, showError } from '../api';
import { useLoad } from '../hooks';
import { cardDeadline, programName } from '../format';
import { Suggestions } from '../types';

/**
 * "Suggested for you" on Discover (students only).
 * Gold: up to 6 programs that fit the student's majors of interest and career interest,
 * each with why. Not Gold: a one-line teaser. No interests yet: where to add them.
 * Counselor picks stay in My programs ("Waiting for you") and come first.
 */
export default function SuggestedForYou({ studentId, saved, onSave }: { studentId: number; saved: Set<number>; onSave: (id: number) => void }) {
  const history = useHistory();
  const s = useLoad<Suggestions>(() => epApi.suggested(studentId), [studentId]);
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  const [all, setAll] = useState(false);
  const [teaserClosed, setTeaserClosed] = useState(() => {
    try {
      return localStorage.getItem('ep.suggest.teaser') === 'closed';
    } catch {
      return false;
    }
  });
  if (!s.data) return null;

  if (!s.data.available) {
    if (teaserClosed) return null;
    return (
      <div className="panel between" style={{ alignItems: 'center', marginBottom: 16 }}>
        <span>
          <span style={{ color: 'var(--ep-ink)', fontWeight: 500 }}>Suggested for you</span> is part of Gold: programs picked from your majors of interest, open to your grade.
        </span>
        <span className="row" style={{ flexWrap: 'nowrap' }}>
          <button type="button" className="btn sm primary" onClick={() => history.push('/payment/upgrade-plan')}>See Gold</button>
          <button
            type="button"
            className="linkish small"
            onClick={() => {
              setTeaserClosed(true);
              try {
                localStorage.setItem('ep.suggest.teaser', 'closed');
              } catch {
                /* private window */
              }
            }}
          >
            Hide
          </button>
        </span>
      </div>
    );
  }

  if (s.data.needs_interests) {
    return (
      <div className="panel between" style={{ alignItems: 'center', marginBottom: 16 }}>
        <span>Add your majors of interest and we will suggest programs that fit them.</span>
        <button type="button" className="btn sm" onClick={() => history.push('/settings')}>Add majors of interest</button>
      </div>
    );
  }

  const list = s.data.programs.filter((p) => !hidden.has(p.id));
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, 3);
  const hide = async (id: number) => {
    setHidden(new Set(hidden).add(id));
    try {
      await epApi.hideSuggestion(studentId, id);
    } catch (e) {
      setHidden(hidden);
      showError(e, 'Could not hide it. Please try again.');
    }
  };
  return (
    <section className="card" style={{ marginBottom: 16 }} aria-labelledby="ep-sfy">
      <div className="between">
        <div>
          <h2 id="ep-sfy">Suggested for you</h2>
          <p className="muted small" style={{ marginTop: 4 }}>
            From {(s.data.interests ?? []).join(', ')}. Open to your grade, with deadlines still ahead.
          </p>
        </div>
      </div>
      <div className="sfy">
        {shown.map((p) => (
          <article key={p.id} className="sfy-item" onClick={() => history.push(`/summer-programs/programs?id=${p.id}`)} tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' ? history.push(`/summer-programs/programs?id=${p.id}`) : undefined)}>
            <span className="chip a" style={{ alignSelf: 'flex-start' }}>{p.reason}</span>
            <h3 style={{ fontSize: 15, marginTop: 10 }}>{programName(p)}</h3>
            {p.institution && p.institution !== programName(p) && <div className="muted small" style={{ marginTop: 2 }}>{p.institution}</div>}
            <div className="muted small" style={{ marginTop: 8 }}>{cardDeadline(p)}</div>
            <div className="row" style={{ marginTop: 'auto', paddingTop: 12 }} onClick={(e) => e.stopPropagation()}>
              <button type="button" className={`btn sm ${saved.has(p.id) ? '' : 'primary'}`} onClick={() => onSave(p.id)} aria-pressed={saved.has(p.id)}>
                {saved.has(p.id) ? 'Saved' : 'Save'}
              </button>
              <button type="button" className="linkish small" onClick={() => hide(p.id)}>Not for me</button>
            </div>
          </article>
        ))}
      </div>
      {list.length > 3 && (
        <button type="button" className="linkish small" style={{ marginTop: 12 }} onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show ${list.length - 3} more`}
        </button>
      )}
    </section>
  );
}
